import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Posts' },
    { id: 'Bed Bugs', name: 'Bed Bugs' },
    { id: 'Rodent Control', name: 'Rodent Control' },
    { id: 'Cockroaches', name: 'Cockroaches' },
    { id: 'Termites', name: 'Termites' },
    { id: 'Ants', name: 'Ants' },
    { id: 'Wasps', name: 'Wasps' },
    { id: 'Fleas', name: 'Fleas' },
    { id: 'General', name: 'General Tips' },
    { id: 'Commercial', name: 'Commercial' },
    { id: 'Prevention', name: 'Prevention' }
  ];

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const url = selectedCategory === 'all' 
          ? `${API_URL}/api/blog`
          : `${API_URL}/api/blog?category=${selectedCategory}`;
        const response = await axios.get(url);
        setPosts(response.data.posts || []);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [selectedCategory]);

  const featuredPosts = posts.filter(post => post.featured);
  const regularPosts = posts.filter(post => !post.featured);

  return (
    <div>
      <SEO 
        title="Pest Control Blog | NYC Exterminator Tips & Guides | Reclaim Pest Control"
        description="Expert pest control tips, guides, and news for NYC homeowners. Learn how to prevent and eliminate bed bugs, rodents, cockroaches, termites & more. Queens, Brooklyn, Manhattan."
        keywords="pest control blog NYC, bed bug tips, rodent prevention, cockroach guide, termite inspection, NYC exterminator advice"
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-700 to-green-600 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Pest Control Blog</h1>
          <p className="text-xl max-w-2xl mx-auto">Expert tips, guides, and news to help NYC homeowners prevent and eliminate pests. Stay informed with the latest pest control strategies.</p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-gray-100 py-4 sticky top-16 z-40">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                  selectedCategory === cat.id
                    ? 'bg-green-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-green-100'
                }`}
                data-testid={`category-filter-${cat.id}`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading posts...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📝</div>
              <h2 className="text-2xl font-bold mb-2">No Posts Found</h2>
              <p className="text-gray-600">Check back soon for new content!</p>
            </div>
          ) : (
            <>
              {/* Featured Posts */}
              {featuredPosts.length > 0 && selectedCategory === 'all' && (
                <div className="mb-12">
                  <h2 className="text-2xl font-bold mb-6 flex items-center">
                    <span className="text-yellow-500 mr-2">⭐</span> Featured Articles
                  </h2>
                  <div className="grid md:grid-cols-3 gap-8">
                    {featuredPosts.map(post => (
                      <Link 
                        key={post.id} 
                        to={`/blog/${post.slug}`}
                        className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition group border-2 border-yellow-400"
                        data-testid={`blog-post-${post.slug}`}
                      >
                        <div className="relative">
                          <img 
                            src={post.featured_image || '/images/services/general.jpg'} 
                            alt={post.title}
                            className="w-full h-48 object-cover group-hover:scale-105 transition duration-300"
                          />
                          <span className="absolute top-3 right-3 bg-yellow-500 text-white text-xs px-2 py-1 rounded-full font-semibold">Featured</span>
                        </div>
                        <div className="p-6">
                          <span className="text-green-600 text-sm font-medium">{post.category}</span>
                          <h3 className="text-xl font-bold mt-2 mb-3 group-hover:text-green-600 transition line-clamp-2">{post.title}</h3>
                          <p className="text-gray-600 text-sm line-clamp-3">{post.excerpt}</p>
                          <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                            <span>{new Date(post.published_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                            <span className="text-green-600 font-medium group-hover:underline">Read More →</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Regular Posts */}
              <div>
                {selectedCategory === 'all' && featuredPosts.length > 0 && (
                  <h2 className="text-2xl font-bold mb-6">All Articles</h2>
                )}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {(selectedCategory === 'all' ? regularPosts : posts).map(post => (
                    <Link 
                      key={post.id} 
                      to={`/blog/${post.slug}`}
                      className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition group"
                      data-testid={`blog-post-${post.slug}`}
                    >
                      <img 
                        src={post.featured_image || '/images/services/general.jpg'} 
                        alt={post.title}
                        className="w-full h-48 object-cover group-hover:scale-105 transition duration-300"
                      />
                      <div className="p-6">
                        <span className="text-green-600 text-sm font-medium">{post.category}</span>
                        <h3 className="text-xl font-bold mt-2 mb-3 group-hover:text-green-600 transition line-clamp-2">{post.title}</h3>
                        <p className="text-gray-600 text-sm line-clamp-3">{post.excerpt}</p>
                        <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                          <span>{new Date(post.published_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          <span className="text-green-600 font-medium group-hover:underline">Read More →</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Need Professional Pest Control?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">Our expert exterminators serve Queens, Manhattan, Brooklyn, and all of NYC. Call now for fast, effective pest elimination!</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:6462099955" className="bg-white text-green-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition" data-testid="blog-cta-call">
              Call (646) 209-9955
            </a>
            <Link to="/contact" className="bg-blue-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-700 transition" data-testid="blog-cta-quote">
              Get Free Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
